import type { StaffSchoolTask } from '../../school_panel/api/types';
import { withTime } from '../../school_panel/lib/dates';

export type TaskStatus = 'scheduled' | 'in_progress' | 'follow_up' | 'done';

export const STATUS_ORDER: TaskStatus[] = ['scheduled', 'in_progress', 'follow_up', 'done'];
export const STATUS_LABEL: Record<TaskStatus, string> = {
  scheduled: 'Scheduled', in_progress: 'In progress', follow_up: 'Needs follow-up', done: 'Done',
};
export const STATUS_HINT: Record<TaskStatus, string> = {
  scheduled: 'opens later', in_progress: 'collecting', follow_up: 'past due', done: 'all in',
};

/** How many students have finished (exam) or read (notice) a task, out of its audience. */
export function taskProgress(t: Pick<StaffSchoolTask, 'kind' | 'counts'>): { done: number; audience: number; pct: number | null } {
  const c = t.counts;
  if (!c) return { done: 0, audience: 0, pct: null };
  const done = (t.kind === 'notice' ? c.read : c.done) ?? 0;
  return { done, audience: c.audience, pct: c.audience > 0 ? Math.round((done * 100) / c.audience) : null };
}

export function taskStatus(t: StaffSchoolTask, now: Date = new Date()): TaskStatus {
  if (new Date(t.opens_at) > now) return 'scheduled';
  if (t.kind === 'exam' && t.due_at && new Date(t.due_at) < now && (t.counts?.missing ?? 0) > 0) return 'follow_up';
  const p = taskProgress(t);
  if (p.audience > 0 && p.done >= p.audience) return 'done';
  return 'in_progress';
}

export interface TaskKpis {
  total: number; exams: number; notices: number;
  /** Σdone / Σaudience over the exams, or null when they have no audience. */
  completionPct: number | null;
  missing: number; missingTasks: number;
  readPct: number | null;
  /** Σread and Σaudience over the notices (the "612 of 826 recipients" caption). */
  noticeRead: number; noticeAudience: number;
}

const pct = (n: number, d: number) => (d > 0 ? Math.round((n * 100) / d) : null);

export function taskKpis(tasks: StaffSchoolTask[]): TaskKpis {
  let examDone = 0, examAud = 0, missing = 0, missingTasks = 0, read = 0, noticeAud = 0;
  const exams = tasks.filter((t) => t.kind === 'exam');
  const notices = tasks.filter((t) => t.kind === 'notice');
  for (const t of exams) {
    if (!t.counts) continue;
    examDone += t.counts.done ?? 0;
    examAud += t.counts.audience;
    const m = t.counts.missing ?? 0;
    missing += m;
    if (m > 0) missingTasks += 1;
  }
  for (const t of notices) {
    if (!t.counts) continue;
    read += t.counts.read ?? 0;
    noticeAud += t.counts.audience;
  }
  return {
    total: tasks.length, exams: exams.length, notices: notices.length,
    completionPct: pct(examDone, examAud), missing, missingTasks, readPct: pct(read, noticeAud),
    noticeRead: read, noticeAudience: noticeAud,
  };
}

const WEEK = 7 * 86_400_000;

/**
 * Change in exam completion, in points: Σdone/Σaudience over the exams due in the last 7 days
 * minus the same over those due in the 7 days before. Null when either window has no audience.
 */
export function completionDelta(tasks: StaffSchoolTask[], now: Date = new Date()): number | null {
  const share = (from: number, to: number): number | null => {
    let done = 0, aud = 0;
    for (const t of tasks) {
      if (t.kind !== 'exam' || !t.due_at || !t.counts) continue;
      const d = new Date(t.due_at).getTime();
      if (d >= from && d < to) { done += t.counts.done ?? 0; aud += t.counts.audience; }
    }
    return aud > 0 ? (done * 100) / aud : null;
  };
  const t0 = now.getTime();
  const recent = share(t0 - WEEK, t0);
  const prior = share(t0 - 2 * WEEK, t0 - WEEK);
  return recent == null || prior == null ? null : Math.round(recent - prior);
}

const ADDIS = 'Africa/Addis_Ababa';

// Date-only deadlines are stored as 23:59 Addis time, so decide and format in
// that zone whatever the viewer's browser timezone is.
export function dueLabel(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: ADDIS, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(d);
  const hh = parts.find((p) => p.type === 'hour')?.value;
  const mm = parts.find((p) => p.type === 'minute')?.value;
  if (hh === '23' && mm === '59') {
    return d.toLocaleDateString(undefined, { timeZone: ADDIS, weekday: 'short', day: 'numeric', month: 'short' });
  }
  return withTime(d, { timeZone: ADDIS, weekday: 'short', day: 'numeric', month: 'short' });
}

/** "Fri 16 Oct" (no time) in Addis time. */
export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { timeZone: ADDIS, weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * The card/table date: "Sent Mon 12 Oct" for a notice, "Opens Mon 19 Oct" for a scheduled
 * exam, otherwise the due date; null when there is nothing to show.
 */
export function taskDateLabel(t: StaffSchoolTask, status: TaskStatus): string | null {
  if (t.kind === 'notice') return `Sent ${shortDate(t.opens_at)}`;
  if (status === 'scheduled') return `Opens ${shortDate(t.opens_at)}`;
  return t.due_at ? dueLabel(t.due_at) : null;
}
